import { ImageWithFallback } from './image/ImageWithFallback';
import { Code2, Lightbulb, Users, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import {
    motion,
    useInView,
    useScroll,
    useTransform,
    useSpring,
    useReducedMotion,
} from 'motion/react';
import { useMemo, useRef } from 'react';
import { Button } from './ui/Button';

export function About() {
    const ref = useRef(null);
    const reduceMotion = useReducedMotion();
    const isInView = useInView(ref, { once: true, margin: '-120px' });

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start'],
    });

    const y1Raw = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
    const y2Raw = useTransform(scrollYProgress, [0, 1], ['0%', '-22%']);
    const y1 = useSpring(y1Raw, { stiffness: 90, damping: 22 });
    const y2 = useSpring(y2Raw, { stiffness: 90, damping: 22 });

    const values = useMemo(
        () => [
            {
                icon: Code2,
                title: 'Moderne Technologien',
                description:
                    'Ich setze auf bewährte, zukunftssichere Technologien wie React und moderne CSS-Frameworks – für maximale Performance.',
            },
            {
                icon: Lightbulb,
                title: 'Kreative Lösungen',
                description:
                    'Jedes Projekt ist einzigartig. Ich entwickle Konzepte, die exakt zu Ihren Zielen, Ihrer Marke und Ihrer Zielgruppe passen.',
            },
            {
                icon: Users,
                title: 'Kundenfokus',
                description:
                    'Transparente Kommunikation & enge Zusammenarbeit – Sie sind jederzeit eingebunden und behalten den Überblick.',
            },
        ],
        []
    );

    const bullets = useMemo(() => ['Strategie + UX', 'Performance & SEO', 'Saubere Übergabe'], []);

    return (
        <section id="ueber-mich" className="relative py-24 px-4 overflow-hidden" ref={ref}>
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-white" />

            {/* Ambient blobs */}
            <motion.div
                className="absolute -top-10 right-[-120px] w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
                style={{
                    background: 'radial-gradient(circle, rgba(59,130,246,0.35), transparent 60%)',
                    y: y1,
                }}
                animate={reduceMotion ? undefined : { scale: [1, 1.08, 1], opacity: [0.22, 0.35, 0.22] }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />
            <motion.div
                className="absolute bottom-[-120px] left-[-140px] w-[560px] h-[560px] rounded-full blur-3xl opacity-30"
                style={{
                    background: 'radial-gradient(circle, rgba(168,85,247,0.35), transparent 60%)',
                    y: y2,
                }}
                animate={reduceMotion ? undefined : { scale: [1, 1.12, 1], opacity: [0.2, 0.34, 0.2] }}
                transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />

            {/* Subtle grid */}
            <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.22) 1px, transparent 1px)',
                    backgroundSize: '64px 64px',
                    maskImage: 'radial-gradient(circle at 50% 35%, black, transparent 70%)',
                }}
                aria-hidden="true"
            />

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    className="text-center mb-14"
                    initial={{ opacity: 0, y: 26 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-white/70 backdrop-blur px-4 py-2 shadow-sm">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white">
              <Sparkles className="h-4 w-4" />
            </span>
                        <span className="text-sm text-gray-700">Über mich</span>
                        <span className="ml-1 rounded-full bg-black/5 px-2 py-0.5 text-xs text-gray-700">
              Fokus: Performance & Qualität
            </span>
                    </div>

                    <h2 className="mt-6 text-5xl md:text-6xl tracking-tight">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Leidenschaft für Code
            </span>
                        <span className="block text-gray-900">trifft auf Design</span>
                    </h2>

                    <p className="mt-5 text-xl text-gray-600 max-w-2xl mx-auto">
                        Ich baue Websites, die nicht nur gut aussehen, sondern schnell laden, Vertrauen schaffen und echte
                        Ergebnisse liefern.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-12 items-center">
                    {/* Image side */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
                    >
                        <div className="relative">
                            {/* Glow frame */}
                            <motion.div
                                className="absolute -inset-4 rounded-3xl blur-2xl opacity-25"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(59,130,246,0.55), rgba(147,51,234,0.55))',
                                }}
                                animate={reduceMotion ? undefined : { scale: [1, 1.04, 1], opacity: [0.18, 0.3, 0.18] }}
                                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                                aria-hidden="true"
                            />

                            {/* Card */}
                            <motion.div
                                className="relative overflow-hidden rounded-3xl border border-gray-200/70 bg-white/70 backdrop-blur shadow-2xl"
                                whileHover={{ y: -6 }}
                                transition={{ type: 'spring', stiffness: 240, damping: 20 }}
                            >
                                <div className="relative">
                                    <ImageWithFallback
                                        src={`${process.env.PUBLIC_URL}/assets/TJ1.jpg`}
                                        alt="Johannsen IT – Portrait"
                                        className="w-full h-[300px] sm:h-[340px] md:h-[420px] lg:h-[480px] object-cover object-top"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-purple-600/10" />
                                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.35),transparent_55%)]" />
                                </div>

                                {/* Floating info strip */}
                                <div className="p-5 md:p-6">
                                    <div className="flex flex-wrap gap-2">
                                        {bullets.map((b) => (
                                            <span
                                                key={b}
                                                className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-3 py-1.5 text-sm text-gray-700 border border-gray-200/70 shadow-sm"
                                            >
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                {b}
                      </span>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Text & cards side */}
                    <motion.div
                        className="space-y-7"
                        initial={{ opacity: 0, x: 40 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.25, ease: 'easeOut' }}
                    >
                        <div className="space-y-4">
                            <p className="text-lg text-gray-700 leading-relaxed">
                                Mit über <span className="font-semibold">6 Jahren Erfahrung</span> in der Webentwicklung helfe ich
                                Unternehmen dabei, ihre digitale Präsenz auf das nächste Level zu bringen – von der ersten Idee bis zum
                                Launch.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                Mein Fokus liegt auf modernen, performanten und benutzerfreundlichen Websites, die nicht nur gut
                                aussehen, sondern auch messbare Ergebnisse liefern.
                            </p>
                        </div>

                        {/* Value cards */}
                        <div className="space-y-4">
                            {values.map((value, index) => {
                                const Icon = value.icon;
                                return (
                                    <motion.div
                                        key={index}
                                        className="group relative overflow-hidden rounded-2xl border border-gray-200/70 bg-white/75 backdrop-blur shadow-lg"
                                        initial={{ opacity: 0, y: 18 }}
                                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                                        transition={{ duration: 0.55, delay: 0.45 + index * 0.1 }}
                                        whileHover={{ y: -4 }}
                                    >
                                        <div className="absolute -top-10 -right-10 h-36 w-36 rounded-full bg-gradient-to-br from-blue-600/10 to-purple-600/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />

                                        <div className="p-6 flex gap-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-md">
                                                    <Icon className="w-6 h-6 text-white" />
                                                </div>
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="text-lg mb-1 text-gray-900">{value.title}</h3>
                                                <p className="text-gray-600 leading-relaxed">{value.description}</p>

                                                <div className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-blue-600 to-purple-600" />
                                                    Schnell, sauber, skalierbar
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* CTA row */}
                        <motion.div
                            className="flex flex-wrap gap-3 pt-2"
                            initial={{ opacity: 0, y: 12 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.85, duration: 0.6 }}
                        >
                            <Button
                                className="group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-md text-white"
                                onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                            >
                                <span className="text-white">Kostenloses Erstgespräch</span>
                                <ArrowRight className="ml-2 h-4 w-4 text-white transition-transform group-hover:translate-x-0.5" />
                            </Button>

                            <Button
                                variant="outline"
                                className="border-2 bg-white/70 backdrop-blur hover:bg-white"
                                onClick={() => document.getElementById('projekte')?.scrollIntoView({ behavior: 'smooth' })}
                            >
                                Projekte ansehen
                            </Button>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
